import { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { TrendingUp, Gift, Lock, CheckCircle2, Clock, Layers } from 'lucide-react';

/**
 * 마이 스테이킹 — 웰컴 스테이킹 현황
 *
 * 에어드롭·추천보상으로 받은 토큰에 연 16% 이자가 붙는다.
 * 원금은 락금고에 그대로 있고, 여기서는 «이자» 를 보여준다.
 * 6개월이 차면 이자는 바로 출금할 수 있고, 원금은 그때부터 매월 20%씩 풀린다.
 */
type Item = {
  id: number; principal: string; source: string; apy: string; months: number;
  start_date: string; mature_date: string; accrued: string; status: string;
  interest_tx: string | null; paid_at: string | null;
};
type Data = {
  enabled: boolean; apy: number; months: number;
  principal: number; earned: number; daily: number; rate: number;
  count: number; startedAt: string | null; nextMature: string | null; items: Item[];
};

const n2 = (v: any, d = 2) =>
  Number(v || 0).toLocaleString('ko-KR', { maximumFractionDigits: d });
const day = (s: string | null) => (s ? String(s).slice(0, 10) : '-');

// 건별 카드 제목 — 출처가 아니라 «상품명» 으로 보여준다
const SOURCE_LABEL: Record<string, string> = {
  bonus: '웰컴 스테이킹 (가입 에어드롭·추천 보너스)',
  airdrop: '웰컴 스테이킹 (가입 에어드롭·추천 보너스)',
  referral: '웰컴 스테이킹 (가입 에어드롭·추천 보너스)',
  reward: '웰컴 스테이킹 (가입 에어드롭·추천 보너스)',
  backfill: '웰컴 스테이킹 (가입 에어드롭·추천 보너스)',
  referral_init: '웰컴 스테이킹 (가입 에어드롭·추천 보너스)',
};


// 하단 지급 내역 줄에 붙는 출처 표시
const ITEM_TAG: Record<string, string> = {
  bonus: '보너스 지급', airdrop: '가입 에어드롭', referral: '추천 보너스',
  referral_init: '추천 보너스 (스테이킹 시작 시 누적)', reward: '보상',
};

export function MyStaking() {
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('handy-jwt-token');
        const r = await fetch('/api/staking/welcome/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await r.json();
        if (!j.success) throw new Error(j.error || '조회 실패');
        setD(j);
      } catch (e: any) { setErr(e.message); }
    })();
  }, []);

  if (err) return <Card><CardContent className="p-6 text-center text-gray-500">{err}</CardContent></Card>;
  if (!d) return <Card><CardContent className="p-6 text-center text-gray-400">불러오는 중…</CardContent></Card>;

  const showWelcome = d.enabled !== false;   // 프로그램 종료 후엔 웰컴 노출 안 함
  if ((!showWelcome || !d.count) && !(d.subs && d.subs.count)) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Gift className="h-10 w-10 mx-auto text-orange-400 mb-3" />
          <p className="font-medium text-gray-900">참여 중인 스테이킹이 없습니다</p>
          <p className="text-sm text-gray-500 mt-1">
          스테이킹 상품 탭에서 로얄 스테이킹에 참여해보세요
        </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {showWelcome && (<>
      {/* 요약 */}
      <Card className="border-0 text-white" style={{ background: '#F97316' }}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">웰컴 스테이킹</p>
              <p className="text-3xl font-light">{n2(d.principal)}</p>
              <p className="text-white/70 text-sm">wHANDY</p>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-sm">{d.months}개월 예상 수익</p>
              <p className="text-2xl font-semibold">{d.apy}%</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center"
               style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.3)' }}>
            <div>
              <p className="text-xs" style={{ opacity: .85, marginBottom: 4 }}>누적 수익</p>
              <p className="font-semibold">+{n2(d.earned)}</p>
            </div>
            <div>
              <p className="text-xs" style={{ opacity: .85, marginBottom: 4 }}>수익률</p>
              <p className="font-semibold">{d.rate.toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-xs" style={{ opacity: .85, marginBottom: 4 }}>하루</p>
              <p className="font-semibold">+{n2(d.daily)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 안내 */}
      <Card>
        <CardContent style={{ padding: '14px 16px' }} className="text-sm text-gray-600">
          <p className="flex items-center gap-2" style={{ lineHeight: 1.5 }}>
            <Lock className="h-4 w-4 shrink-0 text-gray-400" />
            원금은 {d.months}개월 후 매월 20%씩 해제
          </p>
          <p className="flex items-center gap-2" style={{ lineHeight: 1.5, marginTop: 8 }}>
            <TrendingUp className="h-4 w-4 shrink-0 text-gray-400" />
            수익은 원금 × 16%(6개월)를 일할 계산 · 매일 00:30 갱신 · 만기 시 출금
          </p>
        </CardContent>
      </Card>

      {/* 웰컴 스테이킹 — 지급 건이 몇 개든 한 장으로 합산해 보여준다 */}
      {d.items.length > 0 && (() => {
        const principal = d.items.reduce((a, x) => a + Number(x.principal || 0), 0);
        const accrued = d.items.reduce((a, x) => a + Number(x.accrued || 0), 0);
        const starts = d.items.map(x => day(x.start_date)).sort();
        const ends = d.items.map(x => day(x.mature_date)).sort();
        const allPaid = d.items.every(x => x.status === 'paid');
        const anyActive = d.items.some(x => x.status === 'active');
        return (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  웰컴 스테이킹 (가입 에어드롭·추천 보너스)
                </span>
                <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  allPaid ? 'bg-green-100 text-green-700'
                    : anyActive ? 'bg-blue-50 text-blue-600'
                      : 'bg-orange-100 text-orange-700'}`}>
                  {allPaid ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                  {allPaid ? '수익 지급 완료' : anyActive ? '진행 중' : '지급 대기'}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-gray-500">원금</span>
                <span className="text-right font-medium">{n2(principal)} wHANDY</span>
                <span className="text-gray-500">쌓인 수익</span>
                <span className="text-right font-medium text-orange-600">+{n2(accrued)}</span>
                <span className="text-gray-500">시작</span>
                <span className="text-right">{starts[0]}</span>
                <span className="text-gray-500">만기</span>
                <span className="text-right">
                  {ends[0] === ends[ends.length - 1]
                    ? ends[0]
                    : ends[0] + ' ~ ' + ends[ends.length - 1]}
                </span>
              </div>
              {/* 지급 내역 — 스테이킹에 추가된 자산이 날짜별 한 줄씩 자동으로 쌓인다 */}
              <div className="mt-3 pt-2" style={{ borderTop: '1px solid #f3f4f6' }}>
                {[...d.items]
                  .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))
                  .map(it => (
                    <div key={it.id} className="flex items-center justify-between text-xs"
                      style={{ marginTop: 4 }}>
                      <span className="text-gray-500">
                        {day(it.start_date)} · {ITEM_TAG[it.source] || '지급'}
                      </span>
                      <span className="font-medium text-gray-700">+{n2(it.principal)} wHANDY</span>
                    </div>
                  ))}
                {d.items.length > 1 && (
                  <p className="text-xs text-gray-400 mt-2">
                    지급 {d.items.length}건 합산 · 각 건은 지급일부터 6개월
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      </>)}

      {/* 구독 상품 — 회원이 직접 가입한 것 */}
      {d.subs && d.subs.count > 0 && (
        <>
          <div className="pt-2 flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-900">스테이킹 구독</span>
          </div>
          <Card className="border-0 text-white" style={{ background: '#2563EB' }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white/80 text-sm mb-1">맡긴 금액</p>
                  <p className="text-3xl font-light">{n2(d.subs.principal)}</p>
                  <p className="text-white/70 text-sm">wHANDY</p>
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm">누적 수익</p>
                  <p className="text-2xl font-semibold">+{n2(d.subs.earned)}</p>
                  <p className="text-white/70 text-sm">{d.subs.rate.toFixed(2)}% · 하루 +{n2(d.subs.daily)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="space-y-2">
            {d.subs.items.map((it: any) => (
              <Card key={'s' + it.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{it.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      it.status === 'active' ? 'bg-blue-50 text-blue-600' : 'bg-green-100 text-green-700'}`}>
                      {it.status === 'active' ? '진행 중' : '만기'}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                    <span className="text-gray-500">맡긴 금액</span>
                    <span className="text-right font-medium">{n2(it.principal)} wHANDY</span>
                    <span className="text-gray-500">쌓인 수익</span>
                    <span className="text-right font-medium text-blue-600">+{n2(it.accrued)}</span>
                    <span className="text-gray-500">이율</span>
                    <span className="text-right">{Number(it.apy)}% · {it.days}일</span>
                    <span className="text-gray-500">시작</span>
                    <span className="text-right">{day(it.start_date)}</span>
                    <span className="text-gray-500">만기</span>
                    <span className="text-right">{day(it.mature_date)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default MyStaking;
